import React, { Fragment, useEffect, useState } from "react";
import { 
    DefaultEventTypeDetailsModel, DefaultEventTypes, DefaultEventTypeSummaryModel, 
    DefaultSignoffOptions, DefaultVorRequiredOptions, FailedOnlySignoffOption, 
    FailedVorRequiredOption 
} from "pages/fleet/fleet-event-type/wizard/shared/DefaultEventTypeModel";
import PropTypes from "prop-types";
import { UserHasAccess } from "components/user-access";
import { AssetService, FleetService } from "services";
import { NotificationToaster } from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { useValidation } from "hooks/useValidation";
import axios from "axios";
import { FormTextInput } from "components/form-fields";
import { GridLayout } from "components/grid";
import "./EventTypeSummary.css";
import { ShowHide } from "components/layout";
import { isArray } from "underscore";

export function EventTypeSummary({
    model = DefaultEventTypeSummaryModel,
    saving = false,
    globalLoading = false,
    onValidationUpdate = () => { },
    onModelUpdate = () => { return DefaultEventTypeSummaryModel; }
}) {
    const step = 7;
    const checksManageAction = "Checks:Manage";
    const fmsManageAction = "FMS:Manage";
    const userHasFmsManageAction = UserHasAccess(fmsManageAction);
    const userHasChecksManageAction = UserHasAccess(checksManageAction);

    const [loading, setLoading] = useState(true);

    const [detailsModel, setDetailsModel] = useState(DefaultEventTypeDetailsModel);
    const [detailsModelValid, detailsModelErrors, validateDetailsModel] = useValidation();

    const [summaryModel, setSummaryModel] = useState(DefaultEventTypeSummaryModel);

    // Details selected options
    const [selectedEventType, setSelectedEventType] = useState(null);
    const [selectedAssetTypes, setSelectedAssetTypes] = useState(null);
    const [selectedAssetSubTypes, setSelectedAssetSubTypes] = useState(null);
    const [selectedAssetOwnerships, setSelectedAssetOwnerships] = useState(null);
    const [selectedAssetStatuses, setSelectedAssetStatuses] = useState(null);
    const [selectedInitialEventTypeStatus, setSelectedInitialEventTypeStatus] = useState(null);

    // Process selected options
    const [selectedSignoffOption, setSelectedSignoffOption] = useState(null);
    
    // Schedule selected options
    const [selectedFrequencyOption, setSelectedFrequencyOption] = useState(null);
    const [selectedVorRequiredOption, setSelectedVorRequiredOption] = useState(null);


    useEffect(() => {
        if (!userHasFmsManageAction || !loading) {
            return;
        }

        
        setModel(prev => { return {...prev, ...model}; });
        setDetailsModel(prev => { return {...prev, ...model.details}; });
        
        setLocalSelectedOptions(model);
        fetchOptions(model);
    }, [model]);
    
    function setLocalSelectedOptions(internalModel) { 
        const eventType = DefaultEventTypes.find(x => x.isAdHoc === internalModel?.schedule?.adHoc);
        setSelectedEventType(() => { return eventType; });

        const { 
            signoffOptions, 
            vorRequiredOptions 
        } = getOptionsAffectedByFailStatusEnabled(internalModel);

        const tempSelectedSignoffOption = signoffOptions.find(option => option.value === internalModel?.process?.requiresSignoffValue);
        setSelectedSignoffOption(() => { return tempSelectedSignoffOption; });

        const tempSelectedVorRequiredOption = vorRequiredOptions.find(option => option.value === internalModel?.schedule?.vorTimerValue);
        setSelectedVorRequiredOption(() => { return tempSelectedVorRequiredOption; });
    }

    function fetchOptions(internalModel) {
        setLoading(true);

        axios.all([
            FleetService.getEventOptions(),
            AssetService.getAssetOptions()
        ])
        .then(axios.spread(function (eventOptionsResponse, assetOptionsResponse) {
            const assetTypes = assetOptionsResponse.assetTypes.filter(type => internalModel?.details?.additionalProperties?.conditions?.assetTypes.includes(type.id));
            setSelectedAssetTypes(assetTypes);

            if (assetTypes !== null && assetTypes !== undefined && isArray(assetTypes)) {
                const assetSubTypes = [];

                assetTypes.forEach(type => {
                    if (type.subtypes !== null && type.subtypes !== undefined) {
                        const filteredSubtypes = type.subtypes.filter(subtype => internalModel?.details?.additionalProperties?.conditions?.assetSubTypes.includes(subtype.id));
                        assetSubTypes.push(...filteredSubtypes);
                    }
                });

                setSelectedAssetSubTypes(assetSubTypes);
            }

            const assetOwnerships = assetOptionsResponse.assetOwnerships.filter(ownership => internalModel?.details?.additionalProperties?.conditions?.assetOwnerships.includes(ownership.id));
            setSelectedAssetOwnerships(() => { return assetOwnerships; });

            const assetStatuses = assetOptionsResponse.assetStatuses.filter(status => internalModel?.details?.additionalProperties?.conditions?.assetStatuses.includes(status.id));
            setSelectedAssetStatuses(() => { return assetStatuses; });

            const initialEventTypeStatus = eventOptionsResponse.eventStatuses.find(status => status.statusCode === internalModel?.process?.initialEventStatusCode);
            setSelectedInitialEventTypeStatus(() => { return initialEventTypeStatus; });

            const scheduleFrequency = eventOptionsResponse.eventFrequency.find(frequency => frequency.key === internalModel?.schedule?.repeatFrequency);
            setSelectedFrequencyOption(() => { return scheduleFrequency; });
        }))
        .catch(() => {
            NotificationToaster.show(Intent.DANGER, "Could not load the event type options data needed. Please refresh the page.");
        })
        .finally(() => { setLoading(false); });
    }

    function getOptionsAffectedByFailStatusEnabled(internalModel) {
        const tempSignoffOptions = [...DefaultSignoffOptions];
        const tempVorRequiredOptions = [...DefaultVorRequiredOptions];

        if (internalModel?.process?.failStatusEnabled === true) {
            tempSignoffOptions.push(FailedOnlySignoffOption);
            tempVorRequiredOptions.push(FailedVorRequiredOption);
        }

        return {
            signoffOptions: tempSignoffOptions,
            vorRequiredOptions: tempVorRequiredOptions
        };
    }

    useEffect(() => {
        setLoading(globalLoading);
    }, [globalLoading]);

    // Use this to handle input value changes
    function onValueChange(fieldName, value) {
        if (loading) {
            return;
        }
        
        const clonedModel = {
            ...detailsModel,
            [fieldName]: value
        };

        setDetailsModel(clonedModel);
        onModelUpdate(step, clonedModel);
    }

    function setModel(changedModel) {
        setSummaryModel(changedModel);
        setDetailsModel(changedModel.details);
    }

    // publish out validation changes if in wizard mode
    useEffect(() => {
        onValidationUpdate(step, detailsModelValid);
    }, [detailsModelValid])

    useEffect(validate, [detailsModel])
    
    function validate() {
        let validationRules = [
            { fieldName: "name", required: true, minLength: 1, maxLength: 100 }
        ];

        validateDetailsModel(validationRules, detailsModel);
    }
    
    /* Show/Hide Logic */
    function showAssetType() {
        return selectedAssetTypes !== null && selectedAssetTypes !== undefined;
    }

    function showAssetTypeSubtype() {
        return selectedAssetSubTypes !== null && selectedAssetSubTypes !== undefined;
    }

    function showAssetOwnership() {
        return selectedAssetOwnerships !== null && selectedAssetOwnerships !== undefined;
    }

    function showAssetStatus() {
        return selectedAssetStatuses !== null && selectedAssetStatuses !== undefined;
    }

    function showEventHelperText() {
        return summaryModel?.details?.eventHelperText !== null && 
            summaryModel?.details?.eventHelperText !== undefined && 
            summaryModel?.details?.eventHelperText !== "";
    }

    function showCommentHelperText() {
        return summaryModel?.settings?.commentHelperText !== null && 
            summaryModel?.settings?.commentHelperText !== undefined && 
            summaryModel?.settings?.commentHelperText !== "";
    }

    function showDefectHelperText() {
        return summaryModel?.settings?.defectHelperText !== null &&
            summaryModel?.settings?.defectHelperText !== undefined &&
            summaryModel?.settings?.defectHelperText !== "";
    }

    function showDocumentationHelperText() {
        return summaryModel?.settings?.documentationHelperText !== null && 
            summaryModel?.settings?.documentationHelperText !== undefined && 
            summaryModel?.settings?.documentationHelperText !== "";
    }

    function showSignoffHelperText() {
        return summaryModel?.process?.signoffHelperText !== null && 
            summaryModel?.process?.signoffHelperText !== undefined && 
            summaryModel?.process?.signoffHelperText !== "";
    }

    function showScheduled() {
        return summaryModel?.schedule?.adHoc === false;
    }

    function showVorFields() {
        return selectedVorRequiredOption?.value === "2" || selectedVorRequiredOption?.value === "3";
    }

    function showVorPreDays() {
        return summaryModel?.schedule?.vorPreDays !== null && summaryModel?.schedule?.vorPreDays !== undefined;
    }

    function showVorPostDays() {
        return summaryModel?.schedule?.vorPostDays !== null && summaryModel?.schedule?.vorPostDays !== undefined;
    }

    function showCostHelperText() {
        return summaryModel?.costs?.costHelperText !== null && 
            summaryModel?.costs?.costHelperText !== undefined && 
            summaryModel?.costs?.costHelperText !== ""; 
    }

    function showCostDocumentationAttachment() {
        return summaryModel?.costs?.includeCostDocumentation === true;
    }

    function showChecksSection() {
        return userHasChecksManageAction && summaryModel?.checks?.enableChecks === true && summaryModel?.checks?.relatedTemplates?.length > 0;
    }

    /* Show/Hide Logic END */

    function getCommentSettings() {
        let commentSettings = `${summaryModel?.settings?.includeComment === true ? "Enabled" : "Disabled"}, `
        
        if (summaryModel?.settings?.requiresCommentFailedOnly === true) {
            commentSettings += "Only required for failed events";
        }
        else if (summaryModel?.settings?.requiresComment === true) {
            commentSettings += "Required";
        }
        else {
            commentSettings += "Not required";
        }
        
        return commentSettings;
    }

    function getDefectSettings() {
        let defectSettings = `${summaryModel?.settings?.includeDefect === true ? "Enabled" : "Disabled"}, `

        if (summaryModel?.settings?.requiresDefectFailedOnly === true) {
            defectSettings += "Only required for failed events";
        }
        else if (summaryModel?.settings?.requiresDefect === true) {
            defectSettings += "Required";
        }
        else {
            defectSettings += "Not required";
        }

        return defectSettings;
    }

    function getDocumentSettings() {
        let documentSettings = `${summaryModel?.settings?.includeDocumentation === true ? "Enabled" : "Disabled"}, `
        documentSettings += `${summaryModel?.settings?.requiresDocumentationCount ?? 0} document(s) `;

        if (summaryModel?.settings?.requiresDocumentationCount > 0 && summaryModel?.settings?.includeDocumentationFailedOnly === false) {
            documentSettings += "required";
        }
        else if (summaryModel?.settings?.includeDocumentationFailedOnly === true) {
            documentSettings += "required for failed events";
        }

        return documentSettings;
    }

    function getSignoffSettings() {
        let settings = `${selectedSignoffOption?.value === "2" || selectedSignoffOption?.value === "3" ? "Enabled" : "Disabled"}, `
        
        if (summaryModel?.process?.requiresSignoffFailedOnly === true) {
            settings += "Only required for failed events";
        }
        else if (summaryModel?.process?.requiresSignoff === true) {
            settings += "Required";
        }
        else {
            settings += "Not required";
        }
        
        return settings;
    }

    function getEventFrequency() {
        const repeatInterval = summaryModel?.schedule?.repeatInterval ?? 0;
        return `Every ${repeatInterval} ${selectedFrequencyOption?.name ?? ""}`;
    }

    function getVORTimerSettings() {
        let settings = `${selectedVorRequiredOption?.value === "2" || selectedVorRequiredOption?.value === "3" ? "Enabled" : "Disabled"}, `
        
        if (summaryModel?.schedule?.vorTimerFailedOnly === true) {
            settings += "Only required for failed events";
        }
        else if (summaryModel?.schedule?.vorTimer === true) {
            settings += "Required";
        }
        else {
            settings += "Not required";
        }
        
        return settings;
    }

    function getCostSettings() {
        let settings = `${summaryModel?.costs?.includeCosts === true ? "Enabled" : "Disabled"}, `
        
        if (summaryModel?.costs?.requiresCostsFailedOnly === true) {
            settings += "Only required for failed events";
        }
        else if (summaryModel?.costs?.requiresCosts === true) {
            settings += "Required";
        }
        else {
            settings += "Not required";
        }

        return settings;
    }

    function getSupplierSettings() {
        return `${summaryModel?.costs?.includeSuppliers === true ? "Enabled" : "Disabled"}, ${summaryModel?.costs?.requiresSuppliers === true ? "Required" : "Not required"}`
    }

    function getCostDocumentAttachmentSettings() {
        return `${summaryModel?.costs?.requiresCostDocumentationCount ?? 0} document(s) required`;
    }

    function getAssociatedChecks() { 
        const associatedChecks = summaryModel?.checks?.relatedTemplates || [];

        return associatedChecks.map(x => x.name).join(", ");
    }

    function returnNameArray(assetCondition) {
        if (assetCondition && isArray(assetCondition) && assetCondition.length > 0) {
            return assetCondition.map(x => x.name).join(", ");
        }
        return "All";
    }

    return (
        <Fragment>
            <GridLayout numberOfColumns={2} customColumnRatios={[2, 1]} gap={50}>
                <FormTextInput
                    id="input-event-type-name"
                    disabled={saving || loading}
                    loading={loading}
                    value={detailsModel?.name}
                    headingText="Event name"
                    onChange={(event) => onValueChange("name", event.target.value)}
                    dangerHelperText={detailsModelErrors["name"]}
                    fill={true}
                />

                <div className="form-field">
                    <h4>Icon</h4>
                    <div className="summary-icon-container inline-items inline-items-center">
                        <i className={`custom-icon custom-icon-${summaryModel.icon?.eventIconName}`}/>
                    </div>
                </div>
            </GridLayout>

            <ShowHide
                evaluator={showEventHelperText() || showAssetType() || showAssetTypeSubtype() || showAssetOwnership() || showAssetStatus()}
                show={(<h3>Details</h3>)}
            />

            <ShowHide
                evaluator={showEventHelperText()}
                show={(
                    <div className="form-field">
                        <h4 className="summary-field-header">Event Helper Text</h4>
                        <span>{summaryModel?.details?.eventHelperText}</span>
                    </div>
                )}
            />

            <GridLayout numberOfColumns={4}>
                <ShowHide
                    evaluator={showAssetType()}
                    show={(
                        <div className="form-field">
                            <h4 className="summary-field-header">Asset Type</h4>
                            <div>{returnNameArray(selectedAssetTypes)}</div>
                        </div>
                    )}
                />
                <ShowHide
                    evaluator={showAssetTypeSubtype()}
                    show={(
                        <div className="form-field">
                            <h4 className="summary-field-header">Asset Subtype</h4>
                            <div>{returnNameArray(selectedAssetSubTypes)}</div>
                        </div>
                    )}
                />
                <ShowHide
                    evaluator={showAssetOwnership()}
                    show={(
                        <div className="form-field">
                            <h4 className="summary-field-header">Asset Ownership</h4>
                            <div>{returnNameArray(selectedAssetOwnerships)}</div>
                        </div>
                    )}
                />
                <ShowHide
                    evaluator={showAssetStatus()}
                    show={(
                        <div className="form-field">
                            <h4 className="summary-field-header">Asset Status</h4>
                            <div>{returnNameArray(selectedAssetStatuses)}</div>
                        </div>
                    )}
                />
            </GridLayout>

            <h3>Settings</h3>

            <GridLayout numberOfColumns={2} customColumnRatios={[0.64, 2]}>
                <div className="form-field">
                    <h4 className="summary-field-header">Comment</h4>
                    <div>{getCommentSettings()}</div>
                </div>
                <ShowHide
                    evaluator={showCommentHelperText()}
                    show={(
                        <div className="form-field">
                            <h4 className="summary-field-header">Comment Helper Text</h4>
                            <div>{summaryModel?.settings?.commentHelperText}</div>
                        </div>
                    )}
                />
            </GridLayout>

            <GridLayout numberOfColumns={2} customColumnRatios={[0.64, 2]}>
                <div className="form-field">
                    <h4 className="summary-field-header">Defect</h4>
                    <div>{getDefectSettings()}</div>
                </div>
                <ShowHide
                    evaluator={showDefectHelperText()}
                    show={(
                        <div className="form-field">
                            <h4 className="summary-field-header">Defect Helper Text</h4>
                            <div>{summaryModel?.settings?.defectHelperText}</div>
                        </div>
                    )}
                />
            </GridLayout>

            <GridLayout numberOfColumns={2} customColumnRatios={[0.64, 2]}>
                <div className="form-field">
                    <h4 className="summary-field-header">Documents</h4>
                    <div>{getDocumentSettings()}</div>
                </div>
                <ShowHide
                    evaluator={showDocumentationHelperText()}
                    show={(
                        <div className="form-field">
                            <h4 className="summary-field-header">Document Helper Text</h4>
                            <div>{summaryModel?.settings?.documentationHelperText}</div>
                        </div>
                    )}
                />
            </GridLayout>

            <h3>Process</h3>

            <GridLayout numberOfColumns={4}>
                <div className="form-field">
                    <h4 className="summary-field-header">Initial Status</h4>
                    <div>{selectedInitialEventTypeStatus?.name}</div>
                </div>
                <div className="form-field">
                    <h4 className="summary-field-header">Fail Status</h4>
                    <div>{summaryModel?.process?.failStatusEnabled === true ? "Enabled" : "Disabled"}</div>
                </div>
                <div className="form-field">
                    <h4 className="summary-field-header">Sign-off</h4>
                    <div>{getSignoffSettings()}</div>
                </div>
                <div className="form-field">
                    <h4 className="summary-field-header">Sign-off Contacts Only</h4>
                    <div>{summaryModel?.process?.signoffRestricted === true ? "Enabled" : "Disabled"}</div>
                </div>
                <div className="form-field">
                    <h4 className="summary-field-header">Second Sign-off</h4>
                    <div>{summaryModel?.process?.signoffSecondEnabled === true ? "Required" : "Not required"}</div>
                </div>
                <div className="form-field">
                    <h4 className="summary-field-header">Second Sign-off Contacts Only</h4>
                    <div>{summaryModel?.process?.signoffSecondRestricted === true ? "Enabled" : "Disabled"}</div>
                </div>
            </GridLayout>

            <ShowHide
                evaluator={showSignoffHelperText()}
                show={(
                    <div className="form-field">
                        <h4 className="summary-field-header">Sign-off Text</h4>
                        <div>{summaryModel?.process?.signoffHelperText}</div>
                    </div>
                )}
            />

            <h3>Schedule</h3>

            <div className="form-field">
                <h4 className="summary-field-header">Event Type</h4>
                <div>{selectedEventType?.name}</div>
            </div>

            <ShowHide
                evaluator={showScheduled()}
                show={(
                    <GridLayout numberOfColumns={4}>
                        <div className="form-field">
                            <h4 className="summary-field-header">Event Frequency</h4>
                            <div>{getEventFrequency()}</div>
                        </div>
                        <div className="form-field">
                            <h4 className="summary-field-header">Events In Advance (rolling)</h4>
                            <div>{summaryModel?.schedule?.repeatCount}</div>
                        </div>
                        <div className="form-field">
                            <h4 className="summary-field-header">VOR Timer</h4>
                            <div>{getVORTimerSettings()}</div>
                        </div>
                        <ShowHide
                            evaluator={showVorFields()}
                            show={(
                                <Fragment>
                                    <ShowHide
                                        evaluator={showVorPreDays()}
                                        show={(
                                            <div className="form-field">
                                                <h4 className="summary-field-header">Days before event scheduled date</h4>
                                                <div>{summaryModel?.schedule?.vorPreDays}</div>
                                            </div>
                                        )}
                                    />
                                    <ShowHide
                                        evaluator={showVorPostDays()}
                                        show={(
                                            <div className="form-field">
                                                <h4 className="summary-field-header">Days after event scheduled date</h4>
                                                <div>{summaryModel?.schedule?.vorPostDays}</div>
                                            </div>
                                        )}
                                    />
                                </Fragment>
                            )}
                        />
                    </GridLayout>
                )}
            />

            <h3>Costs</h3>

            <GridLayout numberOfColumns={2} customColumnRatios={[0.64, 2]}>
                <div className="form-field">
                    <h4 className="summary-field-header">Costs</h4>
                    <div>{getCostSettings()}</div>
                </div>
                <ShowHide
                    evaluator={showCostHelperText()}
                    show={(
                        <div className="form-field">
                            <h4 className="summary-field-header">Cost comment</h4>
                            <div>{summaryModel?.costs?.costHelperText}</div>
                        </div>
                    )}
                />
            </GridLayout>

             <GridLayout numberOfColumns={4}>
                <div className="form-field">
                    <h4 className="summary-field-header">Suppliers</h4>
                    <div>{getSupplierSettings()}</div>
                </div>
                <ShowHide
                    evaluator={showCostDocumentationAttachment()}
                    show={(
                        <div className="form-field">
                            <h4 className="summary-field-header">Cost Document Attachment</h4>
                            <div>{getCostDocumentAttachmentSettings()}</div>
                        </div>
                    )}
                />
            </GridLayout>

            <ShowHide
                evaluator={showChecksSection()}
                show={(
                    <Fragment>
                        <h3>Checks</h3>

                        <div className="form-field">
                            <h4 className="summary-field-header">Associated Checks</h4>
                            <div>{getAssociatedChecks()}</div>
                        </div>
                    </Fragment>
                )}
            />
        </Fragment>
    )
}

EventTypeSummary.propTypes = {
    model: PropTypes.object,
    saving: PropTypes.bool,
    globalLoading: PropTypes.bool,
    onValidationUpdate: PropTypes.func,
    onModelUpdate: PropTypes.func
};
